import React, { useMemo, VoidFunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { EntityName } from 'record-it-time-common/EntityName';
import { prepareProps } from 'record-it-components/utils/propUtils';
import {
  SearchableSelect,
  SearchableSelectProps
} from 'record-it-components/input/SearchableSelect/SearchableSelect';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { User } from '../../../classes/entities/User/userTypes';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useNullableSelectEntityItems } from '../../../hooks/entity/useNullableSelectEntityItems/useNullableSelectEntityItems';
import { UseSelectEntityItemsUtils } from '../../../hooks/entity/useSelectEntityItems/UseSelectEntityItemsUtils';
import { UserUtils } from '../../../classes/entities/User/UserUtils';

export type UserSelectProps = PropsWithoutChildren<
  Omit<
    SearchableSelectProps<User>,
    | 'getKeyOfItem'
    | 'getDisplayTextOfItem'
    | 'items'
    | 'value'
    | 'onValueChanged'
    | 'label'
  >
> & {
  value: string | null;
  label?: string;
  onValueChanged: (value: string | null) => void;
  filterUsers?: (users: Array<User>) => Array<User>;
};

export const UserSelect: VoidFunctionComponent<UserSelectProps> = React.memo(
  (props) => {
    const { value, label, onValueChanged, filterUsers, ...remainingProps } =
      prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation();
    const users = useEntities(EntityName.User, 'getAll');

    const filteredUsersWithNullValue = useMemo(() => {
      const filteredUsers = filterUsers ? filterUsers(users) : users;
      return [null, ...filteredUsers];
    }, [filterUsers, users]);

    const useSelectEntityItemsReturnValue = useNullableSelectEntityItems({
      value,
      entities: filteredUsersWithNullValue,
      entityNotFoundText: t('components.input.UserSelect.userNotFound'),
      nullValueText: t('components.input.UserSelect.noUser'),
      onValueChanged,
      getDisplayTextOfEntity: UserUtils.displayTextGetter,
      getKeyOfEntity: UserUtils.keyGetter,
      getValueOfEntity: UseSelectEntityItemsUtils.entityIdGetter
    });

    return (
      <SearchableSelect
        label={label != null ? label : t('components.input.UserSelect.label')}
        items={useSelectEntityItemsReturnValue.items}
        value={useSelectEntityItemsReturnValue.selectedItem}
        onValueChanged={useSelectEntityItemsReturnValue.onSelectedItemChanged}
        getDisplayTextOfItem={
          useSelectEntityItemsReturnValue.getDisplayTextOfItem
        }
        getKeyOfItem={useSelectEntityItemsReturnValue.getKeyOfItem}
        data-testid="UserSelect.SearchableSelect"
        {...remainingProps}
      ></SearchableSelect>
    );
  }
);
